import React, { ComponentType, ElementType, forwardRef } from 'react';
import { Box, BoxProps, IconButton, IconButtonProps, SvgIconProps } from '@mui/material';
import { Spinner, SpinnerProps } from '@/src/components/progress/Spinner';
import { ControlsPlayIcon } from '@/src/components/icon/stroke/ControlsPlayIcon';
import { ControlsPauseIcon } from '@/src/components/icon/stroke/ControlsPauseIcon';

export interface PlayPauseButtonProps extends BoxProps<ElementType> {
  /**
   * Boolean to indicate if the media is currently playing
   */
  isPlaying: boolean;
  /**
   * Callback function to handle play/pause button click events.
   */
  onClick: BoxProps<ElementType>['onClick'];
  /**
   * The progress value for the CircularProgress component.
   * Should be a number between 0 and 100.
   */
  progress?: number;
  /**
   * Properties applied to Spinner progress
   */
  progressProps?: Omit<SpinnerProps, 'value'>;
  /**
   * Properties applied to IconButton
   */
  iconButtonProps?: Partial<IconButtonProps>;
  /**
   * Properties applied to Pause Icon component
   */
  pauseIconProps?: SvgIconProps;
  /**
   * Custom component to be used for PauseIcon
   */
  PauseIconComponent?: ComponentType<SvgIconProps>;
  /**
   * Properties applied to Play Icon component
   */
  playIconProps?: SvgIconProps;
  /**
   * Custom component to be used for PlayIcon
   */
  PlayIconComponent?: ComponentType<SvgIconProps>;
}

/**
 * A component that displays a circular progress indicator with a play/pause button.
 */
export const PlayPauseButton: React.FC<PlayPauseButtonProps> = forwardRef(
  (
    {
      progress = 0,
      isPlaying,
      onClick,
      progressProps,
      iconButtonProps,
      pauseIconProps,
      PauseIconComponent,
      playIconProps,
      PlayIconComponent,
      children,
      sx,
      ...rest
    },
    ref,
  ) => {
    const { sx: progressSx, ...restProgress } = progressProps || {};
    const { sx: iconButtonSx, ...restIconButton } = iconButtonProps || {};
    const PauseIcon = PauseIconComponent || ControlsPauseIcon;
    const PlayIcon = PlayIconComponent || ControlsPlayIcon;

    return (
      <Box
        data-testid="PlayPauseIndicator"
        sx={{
          display: 'inline-flex',
          position: 'relative',
          zIndex: '150',
          width: '44px',
          height: '44px',
          ...sx,
        }}
        ref={ref}
        {...rest}
      >
        <Spinner
          value={progress}
          size={44}
          thickness={6}
          sx={{
            position: 'absolute',
            inset: '0',
            zIndex: '20',
            // start from 12'clock instead of 3'
            transform: 'rotate(-90deg)',
            // recolor
            '& .FlowSpinner-circle-primary': {
              stroke: (theme) => theme.palette.brand.secondary,
            },
            '& .FlowSpinner-circle-secondary': {
              stroke: (theme) => theme.palette.common.white,
              // main line is thicker than second one
              strokeWidth: '4px',
            },
            ...progressSx,
          }}
          {...restProgress}
        />
        <IconButton
          data-testid="IndicatorIconButton"
          onClick={onClick}
          color="white"
          size="medium"
          sx={{ position: 'absolute', inset: '0', zIndex: '30', ...iconButtonSx }}
          aria-label={isPlaying ? 'Pause video' : 'Play video'}
          {...restIconButton}
        >
          {isPlaying ? (
            <PauseIcon data-testid="IndicatorPauseIcon" {...pauseIconProps} />
          ) : (
            <PlayIcon data-testid="IndicatorPlayIcon" {...playIconProps} />
          )}
        </IconButton>
      </Box>
    );
  },
);
